import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { bankApi, accountApi } from '@/services/api'
import type { ReconciliationRule, Account } from '@/types'
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import { cn } from '@/lib/utils'

const MATCH_FIELDS = ['description', 'reference', 'payee', 'amount']
const MATCH_TYPES = ['contains', 'equals', 'starts_with', 'ends_with']

const EMPTY_FORM = {
  match_field: 'description',
  match_type: 'contains',
  match_value: '',
  target_account_id: '',
  target_description: '',
  priority: 0,
}

export default function ReconciliationRules() {
  const { clientId } = useParams<{ clientId: string }>()
  const [rules, setRules] = useState<ReconciliationRule[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingRule, setEditingRule] = useState<ReconciliationRule | null>(null)
  const [form, setForm] = useState({ ...EMPTY_FORM })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!clientId) return
    Promise.all([
      bankApi.listRules(clientId),
      accountApi.list(clientId),
    ]).then(([rulesRes, accRes]) => {
      setRules(rulesRes.data.rules)
      setAccounts(accRes.data.accounts)
    }).finally(() => setLoading(false))
  }, [clientId])

  function openCreate() {
    setEditingRule(null)
    setForm({ ...EMPTY_FORM })
    setShowForm(true)
  }

  function openEdit(rule: ReconciliationRule) {
    setEditingRule(rule)
    setForm({
      match_field: rule.match_field,
      match_type: rule.match_type,
      match_value: rule.match_value,
      target_account_id: rule.target_account_id,
      target_description: rule.target_description,
      priority: rule.priority,
    })
    setShowForm(true)
  }

  async function handleSave() {
    if (!clientId || !form.match_value || !form.target_account_id) {
      toast.error('Match value and target account are required.')
      return
    }
    setSaving(true)
    try {
      if (editingRule) {
        await bankApi.updateRule(editingRule.id, form)
        toast.success('Rule updated')
      } else {
        await bankApi.createRule({ ...form, client_ledger_id: clientId })
        toast.success('Rule created')
      }
      const res = await bankApi.listRules(clientId)
      setRules(res.data.rules)
      setShowForm(false)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save rule'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  async function handleToggle(rule: ReconciliationRule) {
    try {
      await bankApi.updateRule(rule.id, { is_active: !rule.is_active })
      const res = await bankApi.listRules(clientId!)
      setRules(res.data.rules)
    } catch {
      toast.error('Failed to update rule')
    }
  }

  async function handleDelete(rule: ReconciliationRule) {
    if (!confirm(`Delete rule "${rule.match_value}"?`)) return
    try {
      await bankApi.deleteRule(rule.id)
      setRules(rules.filter((r) => r.id !== rule.id))
      toast.success('Rule deleted')
    } catch {
      toast.error('Failed to delete rule')
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Reconciliation Rules</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Auto-match bank lines to accounts based on rules</p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <Plus className="w-4 h-4" /> New Rule
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-5">
              {editingRule ? 'Edit Rule' : 'New Rule'}
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">Match Field</label>
                  <select
                    value={form.match_field}
                    onChange={(e) => setForm({ ...form, match_field: e.target.value })}
                    className="input"
                  >
                    {MATCH_FIELDS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="input-label">Match Type</label>
                  <select
                    value={form.match_type}
                    onChange={(e) => setForm({ ...form, match_type: e.target.value })}
                    className="input"
                  >
                    {MATCH_TYPES.map((t) => (
                      <option key={t} value={t}>{t.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="input-label">Match Value</label>
                <input
                  type="text"
                  value={form.match_value}
                  onChange={(e) => setForm({ ...form, match_value: e.target.value })}
                  className="input"
                  placeholder='e.g. "Payroll", "STRIPE", "Netflix"'
                />
              </div>
              <div>
                <label className="input-label">Target Account</label>
                <select
                  value={form.target_account_id}
                  onChange={(e) => setForm({ ...form, target_account_id: e.target.value })}
                  className="input"
                >
                  <option value="">Select account...</option>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="input-label">Auto Description (optional)</label>
                  <input
                    type="text"
                    value={form.target_description}
                    onChange={(e) => setForm({ ...form, target_description: e.target.value })}
                    className="input"
                    placeholder="Leave blank to use original"
                  />
                </div>
                <div>
                  <label className="input-label">Priority</label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 0 })}
                    className="input"
                    min={0}
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6 pt-4 border-t border-surface-100">
              <button onClick={handleSave} disabled={saving} className="btn-primary">
                {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
              </button>
              <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rules.length === 0 ? (
        <div className="card p-12 text-center">
          <ShieldCheck className="w-8 h-8 text-[#CCCED2] mx-auto mb-3" />
          <p className="text-[13px] font-bold text-[#000A1E] mb-1">No rules yet</p>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)]">Rules auto-match bank lines to GL accounts during reconciliation.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-100">
                <th className="table-header">Match</th>
                <th className="table-header">Value</th>
                <th className="table-header">Target Account</th>
                <th className="table-header">Priority</th>
                <th className="table-header">Active</th>
                <th className="table-header text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-100">
              {rules.map((rule) => (
                <tr key={rule.id} className={cn(
                  'hover:bg-surface-50 transition-colors',
                  !rule.is_active && 'opacity-50'
                )}>
                  <td className="table-cell">
                    <span className="badge badge-gray capitalize">{rule.match_field}</span>
                    <span className="text-[13px] text-[rgba(0,10,30,0.75)] ml-1">{rule.match_type.replace('_', ' ')}</span>
                  </td>
                  <td className="table-cell font-mono text-[13px] text-[#000A1E]">{rule.match_value}</td>
                  <td className="table-cell text-[13px] text-[#000A1E]">{rule.target_account_name}</td>
                  <td className="table-cell text-[13px] text-[rgba(0,10,30,0.75)]">{rule.priority}</td>
                  <td className="table-cell">
                    <button onClick={() => handleToggle(rule)} className="text-[rgba(0,10,30,0.75)] hover:text-[#0078C8] transition-colors">
                      {rule.is_active
                        ? <ToggleRight className="w-5 h-5 text-[#0078C8]" />
                        : <ToggleLeft className="w-5 h-5" />
                      }
                    </button>
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(rule)} className="btn-ghost p-1.5" title="Edit">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(rule)} className="btn-ghost p-1.5 text-red-400 hover:text-red-600" title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
